import React, { useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import cn from 'classnames'
// Components
import HeadSeo from '@/components/HeadSeo'
import Footer from '@/components/newFooter'
import Button from '@/components/newButton/Button'
import CitiesList from '@/components/landings/CitiesList'
import SeoLinks from '@/components/landings/SeoLinks'
import OurPartners from '@/components/ourPartners'
import Faqs from '@/components/landings/Faqs'
// Utils
import {
  breedNameTextConverter,
  capitalizeFirstLetterOfTitle,
  slugReverter,
  stateMap,
  useMediaQuery,
  initializeApollo,
} from '@utils'
import cities from '@/utils/us-cities.json'
import { getMetaTagsData, getPageIndex } from '@/components/landings/util'
import { DogsLandingsContext } from '@context/dogsLandingsContext'
import { useRelatedBreeds, useFavourites } from '@hooks'
import styles from './index.module.scss'
// GQL Queries
import {
  GET_FRONTEND_CONFIG_LANDING,
  GET_CATEGORIES,
  STATE_LIST_BY_BREED,
  GET_STATE_LANDING,
  BREED_LIST_BY_STATE,
  GET_LANDINGS_BREED_DETAILS,
  PUPPY_SEARCH,
  BREED_LIST_EXPLORER,
  STATE_LANDING,
  GET_STATE_FACTS_STATE_LANDING,
  GET_NEARBY_DOGS,
  GET_SEO_INDEX_RECORDS,
} from '@graphql'
import { Breed, GetSeoIndexRecordsResponse, LandingType } from '@/types'
import { getPost } from '@/components/blog/lib/cosmic'

// Dynamic
const NearBy = dynamic(() => import('@/components/landings/NearBy'))
const CustomerReview = dynamic(() => import('@/components/search/CustomerReview'))
const Featured = dynamic(() => import('@/components/landings/Featured'))
const LandingCover = dynamic(() => import('@/components/landings/LandingCover'))
const How = dynamic(() => import('@/components/home/How'))
const Why = dynamic(() => import('@/components//home/Why'))
const Facts = dynamic(() => import('@/components/landings/Facts'))
const RelatedBreeds = dynamic(() => import('@/components/landings/RelatedBreeds'))
const Categories = dynamic(() => import('@/components/home/Categories'))
const NationWide = dynamic(() => import('@/components/landings/NationWide'))
const RelatedBreedsDogs = dynamic(() => import('@/components/search/RelatedBreeds'))
const StateHistory = dynamic(() => import('@/components/landings/StateHistory'))
const PuppyQuizBanner = dynamic(() => import('@/components/search/PuppyQuizBanner'))
const SignupModal = dynamic(() => import('@/components/signupModal'))
const Header = dynamic(() => import('@/components/newHeader/Header'))

export default function BreedStateLanding({
  metaTags,
  breedsByState,
  statesByBreed,
  nationwidePuppies,
  allBreedPuppies,
  breedDetails,
  statesData,
  categoriesData,
  variables,
  landingType,
  nearbyDogs,
  faqData,
}) {
  const [showSignup, setShowSignup] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')
  const router = useRouter()
  const breed = breedDetails?.breed || {}
  const { relatedBreeds } = useRelatedBreeds(breed?.id)

  const { favourites, selectedDog, toggleFavourite, refreshUserData } = useFavourites()

  const nationWideDogs = useMemo(() => nationwidePuppies?.search?.rows || [], [nationwidePuppies])
  const breedPuppies = useMemo(
    () =>
      allBreedPuppies?.search?.rows.filter((elem) => {
        return elem.breeder.state === variables?.state
      }),
    [allBreedPuppies, variables?.state]
  )

  const dogs = breedPuppies || []

  // handle state not found or timeout error
  const stateFacts =
    statesData?.state && statesData?.state?.length && statesData.state[0] ? statesData.state[0] : {}

  // Memoize the relatedBreedsDogs array
  const relatedBreedsDogs = useMemo(() => {
    return relatedBreeds
      .map((breed) => ({
        id: breed?.id,
        name: breed?.name,
        slug: breed?.slug,
        dogs: (breed?.dogs ?? []).filter((dog) =>
          [...(variables?.borderingStates ?? []), variables?.state]?.includes(dog?.breeder?.state)
        ),
        dogsAvailable: (breed?.dogs ?? []).filter((dog) =>
          [...(variables?.borderingStates ?? []), variables?.state]?.includes(dog?.breeder?.state)
        ).length,
      }))
      .filter((breed) => breed?.dogs?.length > 0)
  }, [relatedBreeds, variables?.borderingStates, variables?.state])

  // Filter the breeds that doesn't have any dogs
  const filteredBreedDogs = relatedBreedsDogs?.filter((breed) => breed?.dogs?.length > 0)

  const handleUpdateFavourite = (id) => {
    const success = toggleFavourite(id)
    if (!success) {
      setShowSignup(true)
    }
  }

  return (
    <DogsLandingsContext.Provider value={{ variables, landingType }}>
      <HeadSeo metaTags={metaTags} />
      <SignupModal
        isOpen={showSignup}
        close={() => setShowSignup(false)}
        success={() => {
          setShowSignup(false)
          refreshUserData()
          toggleFavourite(selectedDog)
        }}
      />
      <div className={styles.main}>
        <Header />
        <LandingCover
          breed={breed}
          variables={variables}
          landingType={landingType}
          city={capitalizeFirstLetterOfTitle(slugReverter(variables?.citySlug))}
          state={statesData?.state[0]}
        />
        <Featured
          title={
            landingType === 'stateCity'
              ? `Our Puppies in ${capitalizeFirstLetterOfTitle(
                  slugReverter(variables?.citySlug)
                )}, ${variables.state}`
              : `Our ${breedNameTextConverter(breed?.name)} Puppies in ${variables?.stateName}`
          }
          dogs={dogs}
          variables={variables}
          breed={breed}
          landingType={landingType}
          city={capitalizeFirstLetterOfTitle(slugReverter(variables?.citySlug))}
        />

        {landingType !== 'stateCity' && nationWideDogs.length > 0 && (
          <NationWide
            title={`${breedNameTextConverter(breed?.name)} Puppies Nationwide`}
            allDogs={nationWideDogs}
            slug={breed?.slug}
            hasBreeds
          />
        )}

        {landingType !== 'stateCity' && filteredBreedDogs.length !== 0 && (
          <div
            className={cn(styles._sectionRbdogs, styles.relatedBreedsWrapper)}
            id="relatedDogBreeds"
          >
            <RelatedBreedsDogs
              title="Related Breeds Nearby"
              relatedBreeds={filteredBreedDogs}
              favourites={favourites}
              updateFavourite={handleUpdateFavourite}
              state
            />
            <div className={styles.featuredButton}>
              <Button
                title={`Explore puppies in ${variables?.stateName}`}
                theme={['green-solid', 'medium']}
                rightImage="/next-white.svg"
                onClick={() => router.push(`/puppies-for-sale/${variables?.stateSlug}`)}
              />
            </div>
          </div>
        )}

        {landingType === 'stateCity' && Object.entries(nearbyDogs).length !== 0 && (
          <NearBy title="Puppies in Nearby Locations" nearbyDogs={nearbyDogs} isMobile={isMobile} />
        )}

        <div className={styles._sectionRw}>
          {isMobile && <hr />}
          <CustomerReview />
        </div>
        <How />
        <Why />
        <div className={styles._sectionQuiz}>
          <OurPartners />
          <PuppyQuizBanner />
        </div>

        <Facts breed={breed} />

        {Object.entries(breed).length !== 0 && (
          <div id="relatedBreeds" className={styles._sectionRb}>
            <RelatedBreeds relatedBreeds={relatedBreeds} breed={breed} isLanding />
          </div>
        )}
        <div className={styles._sectionCategories}>
          <Categories
            title="Explore dog categories nationwide"
            categories={Object.keys(categoriesData).filter((c) => c !== '__typename')}
            isLanding
          />
        </div>

        <StateHistory state={stateFacts} variables={variables} />

        <div className={styles.listWrapper}>
          <CitiesList
            type={landingType === 'stateCity' ? 'stateCity' : 'breed-state'}
            title={
              breed?.name
                ? `Find ${breed?.name}s in Nearby Cities`
                : 'Find Puppies in Nearby Cities'
            }
            more="View all cities"
            less="View less cities"
            breedName={breed?.slug}
          />

          <SeoLinks
            type="byLocation"
            title={`Find ${breed?.name}s in Other States`}
            emptyMessage="No puppies currently available"
            more="View all locations"
            less="View less locations"
            list={statesByBreed}
          />

          <SeoLinks
            type="byBreed"
            title={`Find Other Breeds in ${variables?.stateName}`}
            emptyMessage={`No other breeds available in ${variables?.stateName}`}
            more="View all breeds"
            less="View less breeds"
            list={breedsByState}
          />
        </div>

        <Faqs data={faqData} />

        <Footer />
      </div>
    </DogsLandingsContext.Provider>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// Redirection map
const redirects = {
  'weimaraner-1': 'weimaraner',
  cavadoodles: 'cavapoo',
  'golden-bernedoodle': 'golden-mountain-doodle',
  'central-asian-shepherd-dog-1': 'central-asian-shepherd-dog',
  'papi-tzu': 'papastzu',
  'Yorkie-Bichon-Mix': 'yorkie-bichon-mix',
  poodle: 'standard-poodle',
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const slug = params['breed-slug']
  const stateCity = Object.keys(stateMap).find((elem) => elem === slug.toString())
  const stateSlug = params?.state

  let variables
  let landingType: LandingType | null = null

  let nationwidePuppies

  // let reservedBreed = []
  let allBreedPuppies = {}
  // Breed landings
  let breedDetails: { breed: Breed | null } = {
    breed: null,
  }

  let statesByBreed = {
    stateListByBreed: [],
  }
  // State landings
  let categoriesData = {}
  const breedsData = {
    breeds: [],
  }
  let nearbyDogs = {}
  let statesData = {}
  let explorer
  let breedsByState = {
    breedListByState: [],
  }

  let seoIndexRecords

  if (slug && stateSlug) {
    // Check if the breed slug is in the redirection map
    landingType = 'breedState'

    if (Object.prototype.hasOwnProperty.call(stateMap, stateSlug)) {
      variables = {
        // breed slug
        slug,
        // state data
        state: stateMap[stateSlug].state,
        stateName: stateMap[stateSlug].name,
        country: 'USA',
        stateSlug,
        borderingStates: stateMap[stateSlug].borderingState,
      }
    } else if (stateCity && stateSlug) {
      const stateCode = stateMap[stateCity]?.state // 'FL'
      const cityExistsInState =
        stateCode && cities?.some((city) => city.state === stateCode && city.slug === stateSlug)

      if (!cityExistsInState) {
        return {
          notFound: true,
        }
      }

      landingType = 'stateCity'
      if (Object.prototype.hasOwnProperty.call(stateMap, slug)) {
        variables = {
          // state slug
          slug,
          state: stateMap[slug].state,
          stateName: stateMap[slug].name,
          country: 'USA',
          stateSlug: slug,
          citySlug: stateSlug,
          borderingStates: stateMap[slug].borderingState,
        }
      }
    } else {
      if (stateSlug === 'samoa') {
        return {
          redirect: {
            destination: `/puppies-for-sale/${slug}/american-samoa`,
            statusCode: 301,
          },
        }
      } else {
        console.error('ENTRE NOT FOUND')
        return {
          notFound: true,
        }
      }
    }

    // Check for redirects
    if (redirects[slug]) {
      return {
        redirect: {
          destination: `/puppies-for-sale/${redirects[slug]}/${stateSlug}`,
          statusCode: 301,
        },
      }
    }
  }

  if (landingType === 'stateCity') {
    const { data: _allBreedPuppies } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: PUPPY_SEARCH,
      variables: {
        state: variables?.state,
        limit: 20,
        sort: 'by-view-score',
      },
    })

    allBreedPuppies = _allBreedPuppies

    const { data: _explorer } = await apolloClient.query({
      query: BREED_LIST_EXPLORER,
      variables: { limit: 12 },
    })

    explorer = _explorer

    const {
      data: { breeds = '', categories = '' },
    } = await apolloClient.query({ query: STATE_LANDING })

    // Breeds
    breedsData.breeds = breeds
    // Categories
    categoriesData = categories
    // States Facts
    const { data: _stateData = '' } = await apolloClient.query({
      query: GET_STATE_FACTS_STATE_LANDING,
      variables: { code: variables?.state },
    })

    statesData = _stateData

    const { data: breedsByStateList } = await apolloClient.query({
      query: BREED_LIST_BY_STATE,
      variables: { state: variables?.state },
    })

    breedsByState = breedsByStateList

    const { data: _nearby } = await apolloClient.query({
      query: GET_NEARBY_DOGS,
      variables: { borderingStates: variables?.borderingStates },
    })

    // grouped dogs by state
    const groupedDogs = {}
    _nearby?.nearbyStateDogs?.forEach((dog) => {
      if (!groupedDogs[dog.breeder.state]) {
        groupedDogs[dog.breeder.state] = []
      }
      groupedDogs[dog.breeder.state].push(dog)
    })

    nearbyDogs = groupedDogs
  }

  if (landingType === 'breedState') {
    try {
      // First fetch breed details since frontendConfig depends on it
      const { data } = await apolloClient.query({
        query: GET_LANDINGS_BREED_DETAILS,
        fetchPolicy: 'cache-first',
        variables: {
          slug: variables?.slug,
        },
      })

      breedDetails = data

      // Parallelize remaining queries for better performance
      const [
        {
          data: { frontendConfig: _landingPhotos },
        },
        { data: _allPuppies },
        {
          data: { categories },
        },
        { data: _stateData },
      ] = await Promise.all([
        apolloClient.query({
          query: GET_FRONTEND_CONFIG_LANDING,
          fetchPolicy: 'cache-first',
          variables: {
            key: `breed-${data?.breed?.id}`,
          },
        }),
        apolloClient.query({
          query: PUPPY_SEARCH,
          fetchPolicy: 'cache-first',
          variables: {
            breedSlug: variables?.slug,
            limit: 5,
            page: 1,
          },
        }),
        apolloClient.query({
          query: GET_CATEGORIES,
          fetchPolicy: 'cache-first',
        }),
        apolloClient.query({
          query: GET_STATE_LANDING,
          fetchPolicy: 'cache-first',
          variables: { code: variables?.state },
        }),
      ])

      const landingPhotos = _landingPhotos?.value
      nationwidePuppies = _allPuppies
      categoriesData = categories
      statesData = _stateData

      // Finally fetch breed puppies with frontendConfigData
      const { data: _breedPuppies } = await apolloClient.query({
        query: PUPPY_SEARCH,
        fetchPolicy: 'cache-first',
        variables: {
          breedSlug: variables?.slug,
          state: variables?.state,
          sort: 'by-view-score',
          frontendConfigData: landingPhotos || '[]',
          limit: 20,
        },
      })

      allBreedPuppies = _breedPuppies
    } catch (error) {
      console.error('BREED + STATE LANDING ERROR', error)
    }

    if (breedDetails?.breed === null) {
      return {
        notFound: true,
      }
    }
  }

  try {
    // Data List
    const { data: breedsByStateList } = await apolloClient.query({
      query: BREED_LIST_BY_STATE,
      fetchPolicy: 'cache-first',
      variables: { state: variables?.state },
    })
    breedsByState = breedsByStateList

    const { data: statesByBreedList } = await apolloClient.query({
      query: STATE_LIST_BY_BREED,
      fetchPolicy: 'cache-first',
      variables: { slug: variables?.slug },
    })
    statesByBreed = statesByBreedList
  } catch (error) {
    console.error('BREED + STATE LANDING ERROR', error)
  }

  try {
    const { data: seoIndexRecordsData = { getSeoIndexRecords: [] } } = (await apolloClient.query({
      query: GET_SEO_INDEX_RECORDS,
    })) as { data: GetSeoIndexRecordsResponse }

    seoIndexRecords = seoIndexRecordsData?.getSeoIndexRecords || []
  } catch (error) {
    console.error('SEO INDEX RECORDS ERROR', error)
  }

  const faqData = await getPost({
    type: 'faqs',
    slug: `${slug}-in-${stateSlug}`,
    content: ['slug', 'title', 'metadata'],
  })

  const ROOT_URL = process?.env?.NEXT_PUBLIC_ROOT_URL

  const { robots = 'index, follow', canonical = '' } = getPageIndex(
    breedDetails?.breed || null,
    variables,
    landingType,
    seoIndexRecords
  )

  const {
    title = '',
    description = '',
    url = '',
    image = '',
    breadCrumbSD = '',
    imageObj = '',
  } = getMetaTagsData(breedDetails?.breed || null, variables, landingType)

  return {
    props: {
      metaTags: {
        title,
        description,
        url,
        image,
        breadCrumbSD,
        imageObj,
        robots: ROOT_URL === 'https://www.mawoopets.com' ? robots : 'noindex, nofollow',
        canonical,
      },
      breedsByState: breedsByState?.breedListByState || {},
      statesByBreed: statesByBreed?.stateListByBreed || {},
      allBreedPuppies: allBreedPuppies || {},
      nationwidePuppies: nationwidePuppies || {},
      breedDetails: breedDetails || {},
      categoriesData: categoriesData || {},
      statesData: statesData || {},
      landingType: landingType || '',
      variables: variables || {},
      faqData: faqData?.metadata?.faqs || [],
      // STATE-CITY
      nearbyDogs: nearbyDogs || {},
      breedsExplorer: explorer?.breedsList || {},
    },
    revalidate: 60 * 60 * 24 * 7, // Revalidate every 7 days
  }
}
